import { useEffect, useRef, VideoHTMLAttributes } from "react";

type Props = VideoHTMLAttributes<HTMLVideoElement> & { src: string };

/**
 * Drop-in replacement for <video src=... autoPlay loop muted playsInline />.
 * Uses preload="none" + IntersectionObserver to defer loading until in view.
 */
const LazyVideo = ({ src, className, style, ...rest }: Props) => {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Do not start loading during react-snap pre-render. Otherwise the
    // serialized HTML ships with <video src="..."> and the browser fetches
    // every demo video (10–15 MB each) on first paint of every page.
    if (typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent)) {
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!el.src) el.src = src;
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src]);

  return (
    <video
      ref={ref}
      loop
      muted
      playsInline
      preload="none"
      className={className}
      style={style}
      {...rest}
    />
  );
};

export default LazyVideo;